import { Box, Button, Callout, Flex, Heading, Spinner, Text, Separator } from '@radix-ui/themes';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO, addDays, isValid } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useDayPrices } from '@/hooks/usePrices';
import { PriceChart } from '@/components/PriceChart';
import { PriceTable } from '@/components/PriceTable';
import { DatePicker } from '@/components/DatePicker';
import { AREA_NAMES } from '@/types';

export function AreaDetail() {
  const { area = '', dateOrType = 'latest' } = useParams<{ area: string; dateOrType: string }>();
  const navigate = useNavigate();
  const areaUpper = area.toUpperCase();

  const { data, loading, error } = useDayPrices(areaUpper, dateOrType);

  const areaName = AREA_NAMES[areaUpper] || areaUpper;
  const isValidArea = Object.keys(AREA_NAMES).includes(areaUpper);

  if (!isValidArea) {
    return (
      <Callout.Root color="red">
        <Callout.Text>
          Ogiltigt område. Giltiga områden: SE1, SE2, SE3, SE4
        </Callout.Text>
      </Callout.Root>
    );
  }

  const today = new Date();
  const tomorrow = addDays(today, 1);

  let selectedDate: Date;
  let isInvalidDate = false;

  if (dateOrType === 'latest') {
    selectedDate = today;
  } else if (dateOrType === 'tomorrow') {
    selectedDate = tomorrow;
  } else {
    const parsedDate = parseISO(dateOrType);
    if (!isValid(parsedDate)) {
      isInvalidDate = true;
      selectedDate = today; // Fallback for rendering
    } else {
      selectedDate = parsedDate;
    }
  }

  const isTomorrowView = dateOrType === 'tomorrow';
  const isLatestView = dateOrType === 'latest';

  const handleDateChange = (date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    const todayStr = format(today, 'yyyy-MM-dd');

    if (formattedDate === todayStr) {
      navigate(`/${area}/latest`);
    } else {
      navigate(`/${area}/${formattedDate}`);
    }
  };

  const handleTomorrowClick = () => {
    navigate(`/${area}/tomorrow`);
  };

  const handleTodayClick = () => {
    navigate(`/${area}/latest`);
  };

  return (
    <Box>
      <Flex direction="column" gap="4" mb="6" px="4">
        <Flex align="center" gap="2">
          <Button variant="ghost" size="1" onClick={() => navigate('/')} aria-label="Tillbaka till startsidan">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Tillbaka
          </Button>
        </Flex>

        <Heading size="7">
          {areaUpper} - {areaName}
        </Heading>

        <Text size="3" color="gray">
          {isTomorrowView
            ? 'Morgondagens priser'
            : format(selectedDate, 'd MMMM yyyy', { locale: sv })}
        </Text>

        <Flex gap="2" wrap="wrap">
          <DatePicker selectedDate={selectedDate} onDateChange={handleDateChange} />
          <Button
            variant={isLatestView ? "solid" : "soft"}
            size="2"
            onClick={handleTodayClick}
          >
            Idag
          </Button>
          <Button
            variant={isTomorrowView ? "solid" : "soft"}
            size="2"
            onClick={handleTomorrowClick}
          >
            Imorgon
          </Button>
        </Flex>
      </Flex>

      {isInvalidDate && (
        <Box px="4">
          <Callout.Root color="red">
            <Callout.Text>
              Ogiltigt datum: {dateOrType}. Använd formatet ÅÅÅÅ-MM-DD.
            </Callout.Text>
          </Callout.Root>
        </Box>
      )}

      {loading && (
        <Flex justify="center" py="9">
          <Spinner size="3" />
        </Flex>
      )}

      {error && (
        <Box px="4">
          <Callout.Root color="amber">
            <Callout.Text>
              {error.code === 'DATA_NOT_AVAILABLE'
                ? 'Morgondagens priser är inte tillgängliga än. De publiceras vanligtvis efter kl. 13:00.'
                : error.error || 'Kunde inte hämta priser'}
            </Callout.Text>
          </Callout.Root>
        </Box>
      )}

      {data && data.prices.length > 0 && (
        <Flex direction="column" gap="6">
          <Box>
            <Heading size="4" mb="3" px="4" className="rt-r-ml-4">
              Prisutveckling
            </Heading>
            <PriceChart prices={data.prices} />
          </Box>

          <Separator size="4" />

          <Box px="4">
            <Heading size="4" mb="3">
              Alla priser
            </Heading>
            <PriceTable prices={data.prices} />
          </Box>
        </Flex>
      )}
    </Box>
  );
}
